/**
 * VideoStatic — custom element for the "Video / Image Static" section.
 *
 * Background behaviour:
 *  • Detects viewport (mobile/desktop) and activates the matching bg group.
 *  • Waits until the section enters the viewport, then tries to autoplay the
 *    background video (muted, no controls).
 *  • If autoplay is blocked (Safari policy, etc.) or the video errors out,
 *    the static image is shown immediately.
 *  • When the video ends naturally the static image fades in; the video
 *    is not replayed until the page reloads.
 *
 * Modal behaviour:
 *  • "Watch Video" button opens a full-screen overlay with the modal video
 *    (sound on, full controls).
 *  • Close button, backdrop click, or Escape close the modal and reset the
 *    video to the beginning.
 *
 * Fade-in behaviour:
 *  • When data-fade="true", elements with .vs__fade become visible
 *    (opacity + translateY) as they enter the viewport, staggered by index.
 */

if (!customElements.get('video-static')) {
  class VideoStatic extends HTMLElement {
    connectedCallback() {
      requestAnimationFrame(() => {
        this._initBackground();
        this._initModal();
        this._initFadeIn();
      });
    }

    // ── Background ──────────────────────────────────────────────────────────

    _initBackground() {
      const isMobile = window.matchMedia('(max-width: 767px)').matches;

      // Pick the active background group based on viewport
      const desktopGroup = this.querySelector('.vs__bg-inner--desktop');
      const mobileGroup  = this.querySelector('.vs__bg-inner--mobile');

      // Mobile group is used on small screens; falls back to desktop if absent
      const activeGroup = isMobile
        ? (mobileGroup && mobileGroup.children.length ? mobileGroup : desktopGroup)
        : desktopGroup;

      if (!activeGroup) return;

      const video = activeGroup.querySelector('.vs__bg-video');
      const img   = activeGroup.querySelector('.vs__bg-img');

      const showImage = () => {
        if (img) img.classList.add('is-visible');
      };

      // No video in this group — show image immediately (it may already be
      // marked is-visible from Liquid, but we ensure it here too)
      if (!video) {
        showImage();
        return;
      }

      // Play the video once the section enters the viewport
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();

          const playPromise = video.play();

          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                // Playing — show image when it ends
                video.addEventListener('ended', showImage, { once: true });
              })
              .catch(() => {
                // Autoplay blocked (Safari, power-save mode, etc.)
                showImage();
              });
          } else {
            // Legacy: no Promise returned — just wire the ended event
            video.addEventListener('ended', showImage, { once: true });
            video.addEventListener('error', showImage,  { once: true });
          }
        },
        { threshold: 0.15 }
      );

      observer.observe(this);
    }

    // ── Modal ───────────────────────────────────────────────────────────────

    _initModal() {
      const openBtn    = this.querySelector('[data-role="open-modal"]');
      const modal      = this.querySelector('.vs__modal');
      const closeBtn   = modal?.querySelector('.vs__modal-close');
      const modalVideo = modal?.querySelector('.vs__modal-video');

      if (!openBtn || !modal) return;

      const open = () => {
        modal.removeAttribute('hidden');
        document.body.style.overflow = 'hidden';
        if (modalVideo) {
          modalVideo.currentTime = 0;
          modalVideo.play().catch(() => {});
        }
      };

      const close = () => {
        modal.setAttribute('hidden', '');
        document.body.style.overflow = '';
        if (modalVideo) {
          modalVideo.pause();
          modalVideo.currentTime = 0;
        }
      };

      openBtn.addEventListener('click', open);
      closeBtn?.addEventListener('click', close);

      // Close on dark backdrop click
      modal.addEventListener('click', (e) => {
        if (e.target === modal) close();
      });

      // Close on Escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !modal.hasAttribute('hidden')) close();
      });
    }

    // ── Fade-in ─────────────────────────────────────────────────────────────

    _initFadeIn() {
      if (this.dataset.fade !== 'true') return;

      const elements = [...this.querySelectorAll('.vs__fade')];
      if (!elements.length) return;

      // Stagger each element's transition by 150 ms
      elements.forEach((el, i) => {
        el.style.transitionDelay = `${i * 0.15}s`;
      });

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          });
        },
        { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
      );

      elements.forEach((el) => observer.observe(el));
    }
  }

  customElements.define('video-static', VideoStatic);
}
